import { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import api from "@/lib/api";
import { MaskedLines, Reveal } from "@/components/motion";
import PropertyCard from "@/components/PropertyCard";
import InquiryForm from "@/components/InquiryForm";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Asterisk } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1515263487990-61b07816b324?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const CHAPTERS = [
  { n: "01", t: "Position", d: "We represent a focused portfolio of commercial assets across Åre, Jämtland and the wider Swedish mountain region — hotels, offices, retail and land." },
  { n: "02", t: "Precision", d: "Every listing is measured, photographed and documented to a professional standard. No stock renders. No guesswork." },
  { n: "03", t: "Partnership", d: "From first enquiry to signed contract, Harry Rubino stays with you personally — protecting your time and negotiating your terms." },
];

export default function Home() {
  const [properties, setProperties] = useState([]);
  const [type, setType] = useState("all");
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  useEffect(() => {
    api.get("/properties", { params: { type } }).then(({ data }) => setProperties(data)).catch(() => {});
  }, [type]);

  return (
    <main>
      {/* HERO */}
      <section ref={heroRef} data-testid="hero" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
          <img src={HERO_IMG} alt="Architecture" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
        </motion.div>

        <div className="relative mx-auto max-w-[1600px] w-full px-6 lg:px-12 pb-16 pt-32">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="overline mb-8 text-white/70">
            Rubino Real Estate · Commercial Property · Sweden
          </motion.div>
          <MaskedLines
            className="font-display font-extrabold tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-8xl text-white"
            lines={["Curating exceptional", "commercial", "property."]}
            delay={0.35}
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-white/85"
          >
            Boutique advisors representing carefully selected hotels, commercial properties and investment opportunities across Sweden.
          </motion.p>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-9 border-y border-border bg-background overflow-hidden">
        <Marquee speed={38} gradient={false} autoFill pauseOnHover>
          {["Hotels", "Offices", "Retail", "Industrial", "Investment", "Advisory"].map((w, i) => (
            <span key={i} className="flex items-center" aria-hidden="true">
              <Asterisk size={24} strokeWidth={1} className="text-muted-foreground/50 mx-8 md:mx-12 shrink-0" />
              <span className={`font-display text-4xl md:text-6xl font-medium tracking-tight ${i % 2 === 1 ? "marquee-outline" : "text-foreground"}`}>
                {w}
              </span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* PRACTICE / MANIFESTO */}
      <section id="practice" className="py-24 lg:py-32 mx-auto max-w-[1600px] px-6 lg:px-12">
        <Reveal className="overline text-muted-foreground mb-16">The Practice</Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-16">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.1} className="border-t border-foreground pt-6">
              <div className="font-mono text-5xl text-muted-foreground mb-8">{c.n}</div>
              <h3 className="font-display text-3xl font-medium tracking-tight mb-4">{c.t}</h3>
              <p className="text-muted-foreground leading-relaxed">{c.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PORTFOLIO */}
      <section id="portfolio" className="py-24 lg:py-32 bg-secondary/40 border-y border-border">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <Reveal>
              <div className="overline text-muted-foreground mb-4">Selected Portfolio</div>
              <h2 className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-none">
                Buildings on<br />the market.
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="w-full md:w-56">
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="filter-type" className="rounded-none border-foreground h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="hotel">Hotel</SelectItem>
                  <SelectItem value="office">Office</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="industrial">Industrial</SelectItem>
                </SelectContent>
              </Select>
            </Reveal>
          </div>

          <div data-testid="property-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
          </div>
          {properties.length === 0 && (
            <p className="text-muted-foreground py-12">No properties in this category yet.</p>
          )}
        </div>
      </section>

      {/* CONTACT PERSON */}
      <section id="team" className="py-24 lg:py-32 mx-auto max-w-[1600px] px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <div className="overline text-muted-foreground mb-6">Your Contact</div>
            <h2 className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-none mb-8">
              An advisor,<br />not an agency.
            </h2>
            <p className="text-muted-foreground leading-relaxed max-w-md">
              Rubino Real Estate is deliberately personal. Every enquiry is handled directly by Harry Rubino — focused entirely on the properties and clients we take on.
            </p>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <div data-testid="contact-person" className="border border-foreground p-8 md:p-12 flex flex-col sm:flex-row gap-8 items-start">
                <div className="shrink-0 h-28 w-28 border border-foreground flex items-center justify-center font-display text-4xl font-extrabold tracking-tighter">
                  HR
                </div>
                <div>
                  <div className="font-display text-3xl font-medium tracking-tight">Harry Rubino</div>
                  <div className="overline text-muted-foreground mt-2">Principal &amp; Founder</div>
                  <div className="mt-6 space-y-2 text-sm">
                    <a href="mailto:harry@rubino.se" data-testid="contact-email" className="block link-underline w-fit">harry@rubino.se</a>
                    <a href="tel:+46733763930" data-testid="contact-phone" className="block link-underline w-fit">+46 733 76 39 30</a>
                  </div>
                  <button
                    data-testid="contact-cta"
                    onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                    className="mt-8 inline-flex border border-foreground px-6 py-3 overline hover-invert hover:bg-foreground hover:text-background"
                  >
                    Send an enquiry
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 lg:py-32 bg-secondary/40 border-t border-border">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-5">
            <div className="overline text-muted-foreground mb-6">Enquire</div>
            <h2 className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-none">
              Start a<br />conversation.
            </h2>
            <p className="text-muted-foreground leading-relaxed max-w-md mt-8">
              Tell us what you're looking for. We'll respond within one business day with a shortlist tailored to your brief.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <InquiryForm property={null} />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
